"use client";
import { useTranslations } from 'next-intl';
import style from './registration.module.scss';
import Input, { InputTypes } from '../../../form-elements/input/Input';
import { checkAllFieldsImproved, ImprovedErrors, isEmail, isRequired } from '@/projectRoot/src/utils/handlers/validationFormHandler';
import { useReducer } from 'react';
import LinkUrl from '../../../general/link/LinkUrl';
import { PhoneNumberUtil } from 'google-libphonenumber';
import { isMatch } from 'date-fns';
import Button from '../../../form-elements/button/Button';
import { useApiClient } from '@/projectRoot/src/contexts/ApiClientContext';
import { writeStorage } from '@rehooks/local-storage';
import { AUTH_IDENTITY_TOKEN } from '@/projectRoot/src/utils/constants/storageKeys';
import { stringToIsoDate } from '@/projectRoot/src/utils/handlers/dateHandler';
import { isPhoneValid, setDefaultErrors, uuid } from '@/projectRoot/src/utils/handlers/miscHandler';
import { useGlobals } from '@/projectRoot/src/contexts/GlobalsContext';

export interface RegistrationState {
    first_name: string;
    last_name: string;
    email: string;
    phone_number: string;
    password: string;
    date_of_birth: string;
    subscription: 1 | 0;
    consent: 1 | 0;
    errors: ImprovedErrors<keyof Omit<RegistrationState, 'errors' | 'disable_submit' | 'subscription'>>;
    disable_submit: boolean;
}

const initialState: RegistrationState = {
    first_name: '',
    last_name: '',
    email: '',
    phone_number: '',
    password: '',
    date_of_birth: '',
    subscription: 0,
    consent: 0,
    errors: {
        first_name: {
          message: '',
          passed_validation: false
        },
        last_name: {
          message: '',
          passed_validation: false
        },
        phone_number: {
          message: '',
          passed_validation: false
        },
        date_of_birth: {
          message: '',
          passed_validation: false
        },
        consent: {
          message: '',
          passed_validation: false
        },
        email: {
            message: '',
            passed_validation: false
        },
        password: {
            message: '',
            passed_validation: false
        }
    },
    disable_submit: true
}

type RegistrationAction =
    { type: "setFirstName", payload: string }
    | { type: "setLastName", payload: string }
    | { type: "setEmail", payload: string }
    | { type: "setPhoneNumber", payload: string }
    | { type: "setPassword", payload: string }
    | { type: "setDateOfBirth", payload: string }
    | { type: "setSubscription", payload: 1 | 0 }
    | { type: "setConsent", payload: 1 | 0 }
    | { type: "setInitialState" }

function reducer(state: RegistrationState, action: RegistrationAction): RegistrationState {
    let stateWithErrors = state;

    switch(action.type){
        case 'setFirstName': {
          if(isRequired(action.payload)){
              stateWithErrors.errors.first_name.passed_validation = false;
              stateWithErrors.errors.first_name.message = 'error_is_required_field';
              stateWithErrors.disable_submit = true;
          }else{
              stateWithErrors.errors.first_name.passed_validation = true;
              stateWithErrors.errors.first_name.message = '';
              stateWithErrors.disable_submit = checkAllFieldsImproved(stateWithErrors);
          }

          return {...stateWithErrors, first_name: action.payload};
        }
        case 'setLastName': {
          if(isRequired(action.payload)){
              stateWithErrors.errors.last_name.passed_validation = false;
              stateWithErrors.errors.last_name.message = 'error_is_required_field';
              stateWithErrors.disable_submit = true;
          }else{
              stateWithErrors.errors.last_name.passed_validation = true;
              stateWithErrors.errors.last_name.message = '';
              stateWithErrors.disable_submit = checkAllFieldsImproved(stateWithErrors);
          }

          return {...stateWithErrors, last_name: action.payload};
        }
        case 'setEmail': {
            if(isRequired(action.payload)){
                stateWithErrors.errors.email.passed_validation = false;
                stateWithErrors.errors.email.message = 'error_is_required_field';
                stateWithErrors.disable_submit = true;
            }else if(!isEmail(action.payload)){
                stateWithErrors.errors.email.passed_validation = false;
                stateWithErrors.errors.email.message = 'error_invalid_email_format';
                stateWithErrors.disable_submit = true;
            }else{
                stateWithErrors.errors.email.passed_validation = true;
                stateWithErrors.errors.email.message = '';
                stateWithErrors.disable_submit = checkAllFieldsImproved(stateWithErrors);
            }

            return {...stateWithErrors, email: action.payload};
        }
        case 'setPhoneNumber': {
          console.log('ulazi ovde: ', action.payload)
          if(isRequired(action.payload)){
            stateWithErrors.errors.phone_number.passed_validation = false;
            stateWithErrors.errors.phone_number.message = 'error_is_required_field';
            stateWithErrors.disable_submit = true;
          }else if(!isPhoneValid(action.payload)){
            stateWithErrors.errors.phone_number.passed_validation = false;
            stateWithErrors.errors.phone_number.message = 'error_invalid_phone_format';
            stateWithErrors.disable_submit = true;
          }else{
            stateWithErrors.errors.phone_number.passed_validation = true;
            stateWithErrors.errors.phone_number.message = '';
            stateWithErrors.disable_submit = checkAllFieldsImproved(stateWithErrors);
          }

          return {...stateWithErrors, phone_number: action.payload};
        }
        case 'setPassword': {
            if(isRequired(action.payload)){
                stateWithErrors.errors.password.passed_validation = false;
                stateWithErrors.errors.password.message = 'error_is_required_field';
                stateWithErrors.disable_submit = true;
            }else{
                stateWithErrors.errors.password.passed_validation = true;
                stateWithErrors.errors.password.message = '';
                stateWithErrors.disable_submit = checkAllFieldsImproved(stateWithErrors);
            }

            return {...stateWithErrors, password: action.payload};
        }
        case 'setDateOfBirth': {
          let currentDate = new Date();
          let targetDate = new Date(currentDate.getFullYear() - 18, currentDate.getMonth(), currentDate.getDate());
          let inputDate = new Date(+action.payload.slice(action.payload.length - 4, action.payload.length), +action.payload.slice(3, 5) - 1, +action.payload.slice(0, 2));
          if(isRequired(action.payload)){
              stateWithErrors.errors.date_of_birth.passed_validation = false;
              stateWithErrors.errors.date_of_birth.message = 'error_is_required_field';
              stateWithErrors.disable_submit = true;
          }else if(!isMatch(action.payload, 'dd.MM.yyyy') || (currentDate.getFullYear() - inputDate.getFullYear()) > 100){
              stateWithErrors.errors.date_of_birth.passed_validation = false;
              stateWithErrors.errors.date_of_birth.message = 'invalid_date';
              stateWithErrors.disable_submit = true;
          }else if(inputDate.getTime() > targetDate.getTime()){
            stateWithErrors.errors.date_of_birth.passed_validation = false;
            stateWithErrors.errors.date_of_birth.message = 'must_be_of_legal_age';
            stateWithErrors.disable_submit = true;
          }else{
              stateWithErrors.errors.date_of_birth.passed_validation = true;
              stateWithErrors.errors.date_of_birth.message = '';
              stateWithErrors.disable_submit = checkAllFieldsImproved(stateWithErrors);
          }

          return {...stateWithErrors, date_of_birth: action.payload};
        }
        case 'setSubscription': {
            stateWithErrors.disable_submit = checkAllFieldsImproved(stateWithErrors);

            return {...stateWithErrors, subscription: action.payload};
        }
        case 'setConsent': {
            if(isRequired(action.payload)){
              stateWithErrors.errors.consent.passed_validation = false;
              stateWithErrors.errors.consent.message = 'error_is_required_field';
              stateWithErrors.disable_submit = true;
            }else{
              stateWithErrors.errors.consent.passed_validation = true;
              stateWithErrors.errors.consent.message = '';
              stateWithErrors.disable_submit = checkAllFieldsImproved(stateWithErrors);
            }

            return {...stateWithErrors, consent: action.payload};
        }
        case 'setInitialState': {
            return { ...initialState, errors: setDefaultErrors(initialState.errors) };
        }
    }
}

interface Props {
  classes?: string[];
}

const Registration = ({classes = []}: Props) => {
  const {apiClient} = useApiClient();
  const t = useTranslations();
  const [state, dispatch] = useReducer(reducer, initialState);
  const {toasts,setToasts} = useGlobals();

  const onInputDate = (date: string) => {
    dispatch({type: 'setDateOfBirth', payload: date});
  }

  const register = async () => {
    if(state.disable_submit) return;
    let [d, m, y] = state.date_of_birth.split('.');

    try{
      const response = await apiClient.auth().register({
        first_name: state.first_name,
        last_name: state.last_name,
        date_of_birth: stringToIsoDate(state.date_of_birth),
        // date_of_birth: format(new Date(+y, +m - 1, +d), 'yyyy-MM-dd'),
        email: state.email,
        password: state.password,
        phone_number: state.phone_number,
        newsletter_subscription: state.subscription
      });
      if(response.data){
          writeStorage(AUTH_IDENTITY_TOKEN, { identity_token: response.data.token, expires_at: response.data.expires_at });
          dispatch({type: 'setInitialState'});
          setTimeout(() => {
            setToasts([...toasts, {
                id: uuid(),
                description: `<p><img style="margin-right: 20px;" src='/img/icons/activated-checkmark.svg' alt='checkmark' />${t('successful_registration')}</p>`,
                initiation_time: new Date().getTime(),
                duration_time: 10000
            }]);
        }, 1000);
      }
    }catch(err){
      console.log(err);
    }
  }

  return (
    <div className={`${style.registration} ${classes.join(' ')}`}>
        <p className={style.title}>
            {t('sign_up_with_email')}
        </p>
        <Input 
          value={state.first_name}
          classes={[style.formGroup]}
          label_text={t('first_name')}
          onInput={(e) => dispatch({type: 'setFirstName', payload: e.currentTarget.value})}
          id='first_name'
          error={{
              has_error: !state.errors.first_name.passed_validation && state.errors.first_name.message.length > 0,
              message: state.errors.first_name.message.length > 0 ? t(`Errors.${state.errors.first_name.message}`) : ''
          }}
        />
        <Input 
          value={state.last_name}
          classes={[style.formGroup]}
          label_text={t('last_name')}
          onInput={(e) => dispatch({type: 'setLastName', payload: e.currentTarget.value})}
          id='last_name'
          error={{
              has_error: !state.errors.last_name.passed_validation && state.errors.last_name.message.length > 0,
              message: state.errors.last_name.message.length > 0 ? t(`Errors.${state.errors.last_name.message}`) : ''
          }}
        />
        <Input 
            value={state.email}
            classes={[style.formGroup]}
            label_text={t('email')}
            onInput={(e) => dispatch({type: 'setEmail', payload: e.currentTarget.value})}
            id='email'
            error={{
                has_error: !state.errors.email.passed_validation && state.errors.email.message.length > 0,
                message: state.errors.email.message.length > 0 ? t(`Errors.${state.errors.email.message}`) : ''
            }}
        />
        <Input 
          value={state.phone_number ?? ''}
          classes={[style.formGroup]}
          label_text={t('phone_number')}
          onPhoneInputAction={(phone, meta) => {'+' + meta.country.dialCode != phone.trim() ? dispatch({type: 'setPhoneNumber', payload: phone}) : () => {}}}
          id='phone_number'
          customPhoneInput
          error={{
              has_error: !state.errors.phone_number.passed_validation && state.errors.phone_number.message.length > 0,
              message: state.errors.phone_number.message.length > 0 ? t(`Errors.${state.errors.phone_number.message}`) : ''
          }}
        />
        <Input 
            value={state.password}
            type={InputTypes.PASSWORD}
            classes={[style.formGroup]}
            label_text={t('password')}
            id='password'
            onInput={(e) => dispatch({type: 'setPassword', payload: e.currentTarget.value})}
            error={{
                has_error: !state.errors.password.passed_validation && state.errors.password.message.length > 0,
                message: state.errors.password.message.length > 0 ? t(`Errors.${state.errors.password.message}`) : ''
            }}
        />
        <Input 
          value={state.date_of_birth}
          classes={[style.formGroup]}
          label_text={t('date_of_birth')}
          onInput={(e) => dispatch({type: 'setDateOfBirth', payload: e.currentTarget.value})}
          onDateInput={onInputDate}
          id='date_of_birth'
          type={InputTypes.DATE}
          date_options={{
            pastDate: true,
            selectedDate: state.date_of_birth
          }}
          error={{
              has_error: !state.errors.date_of_birth.passed_validation && state.errors.date_of_birth.message.length > 0,
              message: state.errors.date_of_birth.message.length > 0 ? t(`Errors.${state.errors.date_of_birth.message}`) : ''
          }}
        />
        <Input 
            value={state.subscription}
            type={InputTypes.CHECKBOX}
            classes={[style.formGroup]}
            label_content={<>{t('subscription')}</>}
            onChange={(e) => dispatch({type: 'setSubscription', payload: state.subscription == 1 ? 0 : 1})}
            checked={state.subscription == 1}
        />
        <Input 
            value={state.consent}
            type={InputTypes.CHECKBOX}
            classes={[style.formGroup]}
            label_content={
              <>
                {t.rich('consent', {
                  link: () => <LinkUrl targetBlanc url='/' text={t('consent_privacy_policy')} />
                })}
              </>
            }
            onChange={(e) => dispatch({type: 'setConsent', payload: state.consent == 1 ? 0 : 1})}
            checked={state.consent == 1}
            error={{
                has_error: !state.errors.consent.passed_validation && state.errors.consent.message.length > 0,
                message: state.errors.consent.message.length > 0 ? t(`Errors.${state.errors.consent.message}`) : ''
            }}
        />
        <Button 
          classes={[style.btn]}
          onClick={register}
          label={t('create_account')}
          style={{opacity: state.disable_submit ? '0.5' : '1'}}
        />
    </div>
  )
};

export default Registration;
